import { Button, Checkbox, CloseButton, Dialog, Portal, Text } from "@chakra-ui/react";
import { createContext, useCallback, useContext, useRef, useState } from "react";

const ConfirmContext = createContext(null)

export function ConfirmProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [rememberedAnswers, setRememberedAnswers] = useState({});
    const promiseResolveRef = useRef(null);
    const dontAskAgainRef = useRef(false);
    const cancelRef = useRef(null);

    const confirm = useCallback((msg) => {
        const text = msg || "Are you sure you want to proceed?";
        if (typeof text === "string" && text in rememberedAnswers) {
            return Promise.resolve(rememberedAnswers[text]);
        }
        setMessage(text);
        dontAskAgainRef.current = false;
        setOpen(true);
        return new Promise(resolve => { promiseResolveRef.current = resolve; });
    }, [rememberedAnswers]);

    const handleCancel = useCallback(() => {
        setOpen(false);
        if (dontAskAgainRef.current && typeof message === "string") {
            setRememberedAnswers(prev => ({ ...prev, [message]: false }));
        }
        setMessage("");
        promiseResolveRef.current && promiseResolveRef.current(false);
    }, [message]);

    const handleConfirm = useCallback(() => {
        setOpen(false);
        if (dontAskAgainRef.current && typeof message === "string") {
            setRememberedAnswers(prev => ({ ...prev, [message]: true }));
        }
        setMessage("");
        promiseResolveRef.current && promiseResolveRef.current(true);
    }, [message]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <Dialog.Root
                role="alertdialog"
                initialFocusEl={() => cancelRef.current}
                open={open}
                onOpenChange={(e) => { if (!e.open) handleCancel(); }}
            >
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content>
                            <Dialog.Header>
                                <Dialog.Title>Confirmation</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body>
                                <Text mb="2">{message}</Text>
                                <Checkbox.Root
                                    size="sm"
                                    defaultChecked={false}
                                    onCheckedChange={(e) => { dontAskAgainRef.current = !!e.checked }}
                                >
                                    <Checkbox.HiddenInput />
                                    <Checkbox.Control />
                                    <Checkbox.Label fontWeight="400">Don't ask me again</Checkbox.Label>
                                </Checkbox.Root>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Dialog.ActionTrigger asChild>
                                    <Button variant="outline" ref={cancelRef} onClick={handleCancel}>Cancel</Button>
                                </Dialog.ActionTrigger>
                                <Button colorPalette="red" onClick={handleConfirm}>Confirm</Button>
                            </Dialog.Footer>
                            <Dialog.CloseTrigger asChild>
                                <CloseButton size="sm" />
                            </Dialog.CloseTrigger>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </ConfirmContext.Provider>
    )
}

export function useConfirm() {
    return useContext(ConfirmContext)
}
